// Google Analytics 4 (GA4) Provider
import React, { useEffect } from 'react'
import ReactGA from 'react-ga4'
import { useLocation } from 'react-router-dom'
import useLocalStorage from '../hooks/useLocalStorage'
import { PageViewInfo } from '../types'
import { fetchEnvironmentVar } from '../utils/miscUtils'

// GA4 Measurement ID
const TRACKING_ID = fetchEnvironmentVar('REACT_APP_GOOGLE_ANALYTICS_ID')

interface GA4ProviderProps {
  children: React.ReactNode
}

// Component to track page views
const PageViewTracker: React.FC = () => {
  const location = useLocation()
  const currentPath = location.pathname + location.search
  const [lastPageView, setLastPageView] = useLocalStorage<PageViewInfo>('last_page_view', { path: '', timestamp: 0 })

  const shouldTrackPageView = (): boolean => {
    if (lastPageView.path !== currentPath) {
      return true
    }

    const timeSinceLastView = Date.now() - lastPageView.timestamp
    const TIME_THRESHOLD = 5 * 60 * 1000 // 5 minutes
    return timeSinceLastView > TIME_THRESHOLD
  }

  useEffect(() => {
    if (shouldTrackPageView()) {
      ReactGA.send({
        hitType: 'pageview',
        page: currentPath,
        title: document.title
      })

      setLastPageView({
        path: currentPath,
        timestamp: Date.now()
      })
    }
  }, [location])

  return null
}

const GA4Provider: React.FC<GA4ProviderProps> = ({ children }) => {
  useEffect(() => {
    // Initialize GA4 once when the provider mounts
    ReactGA.initialize(
      [
        {
          trackingId: TRACKING_ID,
          gaOptions: {
            debug_mode: process.env.NODE_ENV === 'development'
          },
          gtagOptions: {
            debug_mode: true
          }
        }
      ]
    )
  }, [])

  return <>{children}</>
}

export { GA4Provider, PageViewTracker }
export default GA4Provider
